import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TAG_NAME_MAX_LENGTH } from '@wit-support/admin-contracts';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, TagInput, } from '@wit-support/ui';
import { CancelButton } from '@wit-support/ui-forms';
import { listTags } from '../api.js';
/** Сколько ждать после последней буквы, прежде чем спрашивать справочник. */
const SUGGEST_DEBOUNCE_MS = 200;
/**
 * Одинаковый ли набор имён — без учёта порядка. Сохранение без единой
 * правки не должно рождать PUT: это лишний round-trip, лишняя строка аудита и
 * мигание оптимистичного кэша ради ничего.
 */
export function sameTagSet(a, b) {
    if (a.length !== b.length)
        return false;
    const set = new Set(a);
    return b.every((name) => set.has(name));
}
/**
 * Попап подбора тегов диалога: поле `TagInput` кита (подсказки из
 * справочника, создание нового имени прямо из поля) поверх текущего набора.
 * Ничего не пишет сам — набор уходит наружу одной записью по «Сохранить»,
 * а как её провести (оптимистично, с очередью записей) решает панель.
 *
 * Черновик живёт в диалоге и сбрасывается на `value` при каждом открытии:
 * «Отмена» обязана вернуть то, что было, а не то, что успели натыкать.
 */
export function TagPickerDialog({ open, onOpenChange, value, onSave }) {
    const [draft, setDraft] = useState(value);
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    // Сброс черновика — ТОЛЬКО по открытию (`value` намеренно не в
    // зависимостях): открытый диалог не должен терять набранное от фонового
    // перезапроса диалога, который меняет ссылку на `tags`.
    useEffect(() => {
        if (open) {
            setDraft(value);
            setQuery('');
            setDebounced('');
        }
    }, [open]);
    // Справочник спрашиваем не на каждую букву: оператор набирает тег целиком, а
    // запрос на каждый символ — это N-1 лишних round-trip и мигающий список.
    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), SUGGEST_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [query]);
    const suggestionsQuery = useQuery({
        queryKey: ['tags', debounced],
        queryFn: () => listTags(debounced.trim() ? { q: debounced.trim() } : {}),
        enabled: open,
        staleTime: 30_000,
    });
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { size: "md", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "\u0422\u0435\u0433\u0438 \u0434\u0438\u0430\u043B\u043E\u0433\u0430" }), _jsx(DialogDescription, { children: "\u041F\u043E\u0434\u0431\u0435\u0440\u0438\u0442\u0435 \u0438\u0437 \u0441\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0430 \u0438\u043B\u0438 \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043D\u043E\u0432\u043E\u0435 \u0438\u043C\u044F \u2014 \u043E\u043D\u043E \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u0432 \u0441\u043B\u043E\u0432\u0430\u0440\u0435 \u043F\u0440\u0438 \u0441\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u0438\u0438." })] }), _jsx(TagInput, { value: draft, suggestions: suggestionsQuery.data ?? [], query: query, onQueryChange: setQuery, onChange: setDraft, loading: suggestionsQuery.isFetching, 
                    // Предел контракта — на клиенте, чтобы длинное имя отсекалось в поле,
                    // а не возвращалось 400 с телом ZodError в тосте.
                    maxLength: TAG_NAME_MAX_LENGTH, placeholder: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0442\u0435\u0433\u2026", "aria-label": "\u0422\u0435\u0433\u0438" }), _jsxs(DialogFooter, { children: [_jsx(CancelButton, { onClick: () => onOpenChange(false) }), _jsx(Button, { type: "button", onClick: () => {
                                if (!sameTagSet(draft, value))
                                    onSave(draft);
                                onOpenChange(false);
                            }, children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] }) }));
}
//# sourceMappingURL=tag-picker-dialog.js.map